import React, { useState } from 'react';
import { Download, FileText, Loader2, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@services/api';
import Button from '@components/common/Button';
import Alert from '@components/common/Alert';

const DataExport: React.FC = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExportData = async () => {
    setExportError(null);
    setExportSuccess(false);
    setIsExporting(true);

    try {
      // Fetch export data from API
      const response = await authAPI.exportData();

      if (!response.success) {
        throw new Error('Failed to generate data export');
      }

      // Create blob from JSON data
      const jsonString = JSON.stringify(response.data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `pixelshare-data-export-${timestamp}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      toast.success('Data export downloaded successfully');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to export data';
      setExportError(errorMessage);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center">
          <FileText size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Export Your Data
          </h2>
          <p className="text-sm text-gray-500">
            Download a copy of your personal data (GDPR compliant)
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Info Box */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <FileText size={16} className="text-gray-500" />
            What&apos;s included in your export?
          </h4>
          <ul className="text-sm text-gray-600 space-y-1.5 ml-6">
            <li className="flex items-start gap-2">
              <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
              <span>Profile information (name, email, account settings)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
              <span>Order history (purchases, payment summaries)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
              <span>Activity data (login history, verification status)</span>
            </li>
          </ul>
        </div>

        {/* GDPR Notice */}
        <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertTriangle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">GDPR Article 20 - Right to Data Portability</p>
            <p className="mt-1">
              This export provides your data in a machine-readable format (JSON)
              that you can use to transfer your information to another service.
            </p>
          </div>
        </div>

        {exportError && (
          <Alert
            type="error"
            message={exportError}
            dismissible
            onDismiss={() => setExportError(null)}
          />
        )}

        {exportSuccess && (
          <Alert
            type="success"
            message="Your data has been exported successfully. Check your downloads folder."
            dismissible
            onDismiss={() => setExportSuccess(false)}
          />
        )}

        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={handleExportData}
            loading={isExporting}
            disabled={isExporting}
            icon={isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          >
            {isExporting ? 'Generating Export...' : 'Download My Data'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataExport;
